import { type MouseEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSiteContent } from "@/context/SiteContentContext";
import { buildWhatsAppLink } from "@/lib/cms";

const footerLinks = [
  { label: "Inicio", href: "#inicio" },
  { label: "Servicios", href: "#servicios" },
  { label: "Proyectos", href: "#proyectos" },
  { label: "Tecnologías", href: "#tecnologias" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Contacto", href: "#contacto" },
];

export default function Footer() {
  const { settings, sections, socialLinks } = useSiteContent();
  const navigate = useNavigate();
  const location = useLocation();
  const content = sections.footer || {};

  const isHome = location.pathname === "/" || location.pathname === "";

  const scrollToId = (id: string) => {
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempts < 25) {
        attempts += 1;
        window.setTimeout(tryScroll, 120);
      }
    };
    tryScroll();
  };

  const handleNavClick = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    const id = href.replace("#", "");
    event.preventDefault();
    if (isHome) {
      scrollToId(id);
    } else {
      navigate("/");
      window.setTimeout(() => scrollToId(id), 180);
    }
  };

  const brand = String(settings.brand || "MARK DIRECT COMPANY");
  const words = brand.trim().split(" ");
  const main = words.slice(0, -1).join(" ") || brand;
  const rest = words.length > 1 ? words[words.length - 1] : "";

  const logoDarkUrl =
    typeof settings.logo_dark_url === "string" && settings.logo_dark_url.length > 0
      ? settings.logo_dark_url
      : null;
  const logoUrl =
    typeof settings.logo_url === "string" && settings.logo_url.length > 0
      ? settings.logo_url
      : null;
  const footerLogo = logoDarkUrl || logoUrl;

  const whatsappNumber = String(settings.whatsapp_number || "");
  const whatsappMessage = String(settings.whatsapp_message || "");

  return (
    <footer className="relative bg-primary-950 text-background-300">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <a
              href="#inicio"
              onClick={(e) => handleNavClick(e, "#inicio")}
              className="flex items-center gap-2.5"
            >
              {footerLogo ? (
                <img
                  src={footerLogo}
                  alt={brand}
                  className="h-10 w-auto max-w-[160px] object-contain"
                />
              ) : (
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 text-background-50 font-bold text-sm">
                  MD
                </span>
              )}
              <span className="flex flex-col leading-none">
                <span className="font-heading font-bold text-background-50 tracking-tight">
                  {main}
                </span>
                {rest && (
                  <span className="text-[10px] tracking-[0.25em] text-background-400 font-medium">
                    {rest}
                  </span>
                )}
              </span>
            </a>
            <p className="mt-5 text-sm text-background-400 leading-relaxed">
              {String(content.description || "Soluciones digitales para negocios que quieren crecer.")}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">
              Navegación
            </h4>
            <ul className="grid grid-cols-2 gap-2">
              {footerLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-sm text-background-400 hover:text-background-50 transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">
              Síguenos
            </h4>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social) => {
                const isWhatsApp = social.nombre.toLowerCase() === "whatsapp";
                const url = isWhatsApp
                  ? buildWhatsAppLink(whatsappNumber, whatsappMessage)
                  : social.url || "";
                return (
                  <a
                    key={social.id}
                    href={url || "#"}
                    target={url ? "_blank" : undefined}
                    rel={url ? "noopener noreferrer" : undefined}
                    aria-label={social.nombre}
                    title={social.nombre}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/10 text-background-200 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer"
                  >
                    <i className={`${social.icono || "ri-link"} text-base`} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-background-50/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-background-400">
            {String(content.copyright || "© 2026 MARK DIRECT COMPANY. Todos los derechos reservados.")}
          </p>
          <p className="text-xs text-background-500">
            {String(content.tagline || "Convertimos ideas en soluciones digitales.")}
          </p>
        </div>
      </div>
    </footer>
  );
}